import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Upload, X, Plus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';

const AuthPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('signup');
  
  // Sign Up Form State
  const [signupData, setSignupData] = useState({
    fullName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    profilePhoto: null,
    selectedDepartments: [],
    location: { state: '', city: '' },
    bio: '',
    pastProjects: [{ projectName: '', role: '', year: '' }]
  });

  // Sign In Form State
  const [signinData, setSigninData] = useState({
    email: '',
    password: ''
  });

  const departments = [
    'Art Department',
    'Camera Department',
    'Sound Department',
    'Direction',
    'Makeup & Hair',
    'SFX / VFX',
    'Production',
    'Assistant Directors',
    'Lighting Department',
    'Post Production',
    'Script Department',
    'Editing'
  ];

  const states = ['Maharashtra', 'Delhi', 'Karnataka', 'Tamil Nadu', 'West Bengal', 'Telangana', 'Rajasthan', 'Kerala'];

  const handleDepartmentToggle = (dept) => {
    if (signupData.selectedDepartments.includes(dept)) {
      setSignupData({
        ...signupData,
        selectedDepartments: signupData.selectedDepartments.filter(d => d !== dept)
      });
    } else {
      setSignupData({
        ...signupData,
        selectedDepartments: [...signupData.selectedDepartments, dept]
      });
    }
  };

  const addProject = () => {
    setSignupData({
      ...signupData,
      pastProjects: [...signupData.pastProjects, { projectName: '', role: '', year: '' }]
    });
  };

  const removeProject = (index) => {
    setSignupData({
      ...signupData,
      pastProjects: signupData.pastProjects.filter((_, i) => i !== index)
    });
  };

  const updateProject = (index, field, value) => {
    const updated = [...signupData.pastProjects];
    updated[index][field] = value;
    setSignupData({ ...signupData, pastProjects: updated });
  };

  const handleSignUp = (e) => {
    e.preventDefault();
    if (signupData.password !== signupData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    toast.success('Account created successfully! Welcome to Shoot Base.');
    setTimeout(() => navigate('/'), 2000);
  };

  const handleSignIn = (e) => {
    e.preventDefault();
    toast.success('Signed in successfully!');
    setTimeout(() => navigate('/'), 2000);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="border-b border-border backdrop-blur-sm bg-background/95 sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/')}
                className="hover:bg-accent/10 hover:text-accent"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <button
                onClick={() => navigate('/')}
                className="flex items-center gap-3 hover:opacity-80 transition-opacity bg-transparent border-none cursor-pointer"
              >
                <img src="/logo.png" alt="Shoot Base" className="h-14 md:h-20 w-auto object-contain" />
                <h1 className="text-xl md:text-2xl font-bold text-accent" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Shoot Base
                </h1>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Auth Section */}
      <section className="py-12 bg-gradient-to-b from-orange-ultralight to-background min-h-[calc(100vh-80px)]">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl mx-auto"
          >
            <Card className="bg-card border-border shadow-xl">
              <CardContent className="p-8">
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                  <TabsList className="grid w-full grid-cols-2 mb-6">
                    <TabsTrigger value="signup">Join Now</TabsTrigger>
                    <TabsTrigger value="signin">Sign In</TabsTrigger>
                  </TabsList>

                  {/* Sign Up Tab */}
                  <TabsContent value="signup">
                    <div className="text-center mb-6">
                      <h2 className="text-3xl font-bold text-foreground mb-2">Join Shoot Base</h2>
                      <p className="text-muted-foreground">Create your professional profile and get discovered</p>
                    </div>

                    <form onSubmit={handleSignUp} className="space-y-6">
                      {/* Mandatory Fields */}
                      <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-foreground">Basic Information *</h3>
                        
                        <div>
                          <label className="text-sm font-medium mb-2 block">Full Name *</label>
                          <Input
                            required
                            value={signupData.fullName}
                            onChange={(e) => setSignupData({ ...signupData, fullName: e.target.value })}
                            placeholder="John Doe"
                          />
                        </div>

                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm font-medium mb-2 block">Email Address *</label>
                            <Input
                              required
                              type="email"
                              value={signupData.email}
                              onChange={(e) => setSignupData({ ...signupData, email: e.target.value })}
                              placeholder="john@example.com"
                            />
                          </div>
                          <div>
                            <label className="text-sm font-medium mb-2 block">Phone Number *</label>
                            <Input
                              required
                              type="tel"
                              value={signupData.phone}
                              onChange={(e) => setSignupData({ ...signupData, phone: e.target.value })}
                              placeholder="+91 98765 43210"
                            />
                          </div>
                        </div>

                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm font-medium mb-2 block">Password *</label>
                            <Input
                              required
                              type="password"
                              value={signupData.password}
                              onChange={(e) => setSignupData({ ...signupData, password: e.target.value })}
                              placeholder="••••••••"
                            />
                          </div>
                          <div>
                            <label className="text-sm font-medium mb-2 block">Confirm Password *</label>
                            <Input
                              required
                              type="password"
                              value={signupData.confirmPassword}
                              onChange={(e) => setSignupData({ ...signupData, confirmPassword: e.target.value })}
                              placeholder="••••••••"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Optional Fields */}
                      <div className="space-y-4 pt-4 border-t border-border">
                        <h3 className="text-lg font-semibold text-foreground">Professional Details (Optional)</h3>
                        <p className="text-sm text-muted-foreground">You can skip and complete these later from your profile</p>

                        {/* Profile Photo */}
                        <div>
                          <label className="text-sm font-medium mb-2 block">Profile Photo</label>
                          <div className="flex items-center gap-4">
                            <Button type="button" variant="outline" className="gap-2">
                              <Upload className="w-4 h-4" />
                              Upload Photo
                            </Button>
                            <span className="text-sm text-muted-foreground">JPG, PNG up to 5MB</span>
                          </div>
                        </div>

                        {/* Departments */}
                        <div>
                          <label className="text-sm font-medium mb-3 block">Department(s)</label>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 p-4 bg-muted/30 rounded-lg border border-border max-h-60 overflow-y-auto">
                            {departments.map(dept => (
                              <div key={dept} className="flex items-center space-x-2">
                                <Checkbox 
                                  id={dept}
                                  checked={signupData.selectedDepartments.includes(dept)}
                                  onCheckedChange={() => handleDepartmentToggle(dept)}
                                />
                                <label htmlFor={dept} className="text-sm font-medium cursor-pointer">
                                  {dept}
                                </label>
                              </div>
                            ))}
                          </div>
                          {signupData.selectedDepartments.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-3">
                              {signupData.selectedDepartments.map(dept => (
                                <Badge key={dept} className="bg-accent/10 text-accent border-accent/30">
                                  {dept}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Location */}
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm font-medium mb-2 block">State</label>
                            <Input
                              value={signupData.location.state}
                              onChange={(e) => setSignupData({ ...signupData, location: { ...signupData.location, state: e.target.value }})}
                              placeholder="Maharashtra"
                            />
                          </div>
                          <div>
                            <label className="text-sm font-medium mb-2 block">City</label>
                            <Input
                              value={signupData.location.city}
                              onChange={(e) => setSignupData({ ...signupData, location: { ...signupData.location, city: e.target.value }})}
                              placeholder="Mumbai"
                            />
                          </div>
                        </div>

                        {/* Bio */}
                        <div>
                          <label className="text-sm font-medium mb-2 block">Bio / Short Description</label>
                          <Textarea
                            value={signupData.bio}
                            onChange={(e) => setSignupData({ ...signupData, bio: e.target.value })}
                            placeholder="Tell us about your experience and expertise..."
                            rows={3}
                          />
                        </div>

                        {/* Past Projects */}
                        <div>
                          <label className="text-sm font-medium mb-3 block">Past Projects</label>
                          {signupData.pastProjects.map((project, index) => (
                            <div key={`project-${index}-${project.title || 'new'}`} className="mb-3 p-4 bg-muted/30 rounded-lg border border-border">
                              <div className="flex justify-between items-center mb-3">
                                <span className="text-sm font-medium">Project {index + 1}</span>
                                {signupData.pastProjects.length > 1 && (
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => removeProject(index)}
                                  >
                                    <X className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                              <div className="grid md:grid-cols-3 gap-3">
                                <Input
                                  placeholder="Project Name"
                                  value={project.projectName}
                                  onChange={(e) => updateProject(index, 'projectName', e.target.value)}
                                />
                                <Input
                                  placeholder="Your Role"
                                  value={project.role}
                                  onChange={(e) => updateProject(index, 'role', e.target.value)}
                                />
                                <Input
                                  placeholder="Year"
                                  type="number"
                                  value={project.year}
                                  onChange={(e) => updateProject(index, 'year', e.target.value)}
                                />
                              </div>
                            </div>
                          ))}
                          <Button type="button" variant="outline" onClick={addProject} className="w-full gap-2">
                            <Plus className="w-4 h-4" />
                            Add Another Project
                          </Button>
                        </div>
                      </div>

                      <Button type="submit" className="w-full bg-accent hover:bg-accent/90 text-white py-6 text-lg">
                        Create Account
                      </Button>

                      <p className="text-center text-sm text-muted-foreground">
                        By signing up, you agree to our{' '}
                        <button type="button" onClick={() => navigate('/terms-of-service')} className="text-accent hover:underline">Terms of Service</button>
                        {' '}and{' '}
                        <button type="button" onClick={() => navigate('/privacy-policy')} className="text-accent hover:underline">Privacy Policy</button>
                      </p>
                    </form>
                  </TabsContent>

                  {/* Sign In Tab */}
                  <TabsContent value="signin">
                    <div className="text-center mb-6">
                      <h2 className="text-3xl font-bold text-foreground mb-2">Welcome Back</h2>
                      <p className="text-muted-foreground">Sign in to your Shoot Base account</p>
                    </div>

                    <form onSubmit={handleSignIn} className="space-y-6">
                      <div>
                        <label className="text-sm font-medium mb-2 block">Email Address</label>
                        <Input
                          required
                          type="email"
                          value={signinData.email}
                          onChange={(e) => setSigninData({ ...signinData, email: e.target.value })}
                          placeholder="john@example.com"
                        />
                      </div>

                      <div>
                        <label className="text-sm font-medium mb-2 block">Password</label>
                        <Input
                          required
                          type="password"
                          value={signinData.password}
                          onChange={(e) => setSigninData({ ...signinData, password: e.target.value })}
                          placeholder="••••••••"
                        />
                        <button type="button" className="text-sm text-accent hover:underline mt-2">Forgot password?</button>
                      </div>

                      <Button type="submit" className="w-full bg-accent hover:bg-accent/90 text-white py-6 text-lg">
                        Sign In
                      </Button>

                      <p className="text-center text-sm text-muted-foreground">
                        Don't have an account?{' '}
                        <button type="button" onClick={() => setActiveTab('signup')} className="text-accent hover:underline font-medium">Join Now</button>
                      </p>
                    </form>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AuthPage;